import { useCallback, useMemo, useState } from 'react'

type SelectableEntity = {
    id: string
}

export function useBulkSelection<T extends SelectableEntity>(pageItems: T[]) {
    const [selectedItems, setSelectedItems] = useState<T[]>([])
    /**
     * When true, the user chose "select all matching" beyond the current page.
     * Callers should treat `selectedItems` as the full matching set (or refetch
     * ids before bulk ops if the set may be stale).
     */
    const [isAllMatchingSelected, setIsAllMatchingSelected] = useState(false)

    const toggleSelected = useCallback((item: T) => {
        setIsAllMatchingSelected(false)
        setSelectedItems(prev =>
            prev.some(selectedItem => selectedItem.id === item.id)
                ? prev.filter(selectedItem => selectedItem.id !== item.id)
                : [...prev, item],
        )
    }, [])

    const selectAllOnPage = useCallback((items: T[]) => {
        setIsAllMatchingSelected(false)
        setSelectedItems(prev => {
            const pageIds = new Set(items.map(item => item.id))
            const allSelected = items.every(item =>
                prev.some(selectedItem => selectedItem.id === item.id),
            )

            if (allSelected) {
                return prev.filter(item => !pageIds.has(item.id))
            }

            const existingIds = new Set(prev.map(item => item.id))
            const itemsToAdd = items.filter(item => !existingIds.has(item.id))

            return [...prev, ...itemsToAdd]
        })
    }, [])

    const selectExactItems = useCallback((items: T[]) => {
        setSelectedItems(items)
        setIsAllMatchingSelected(true)
    }, [])

    const clearSelected = useCallback(() => {
        setSelectedItems([])
        setIsAllMatchingSelected(false)
    }, [])

    const areAllOnPageSelected = useMemo(() => {
        if (pageItems.length === 0) return false

        return pageItems.every(pageItem =>
            selectedItems.some(selectedItem => selectedItem.id === pageItem.id),
        )
    }, [pageItems, selectedItems])

    return {
        selectedItems,
        setSelectedItems,
        toggleSelected,
        selectAllOnPage,
        selectExactItems,
        clearSelected,
        areAllOnPageSelected,
        isAllMatchingSelected,
        setIsAllMatchingSelected,
    }
}
